import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { ContactRequest } from '../../types/api.types';
import { getContacts, updateContactStatus, deleteContact } from '../../services/storage';

const STATUS_COLORS: Record<ContactRequest['status'], 'error' | 'warning' | 'success'> = {
  new: 'error',
  read: 'warning',
  replied: 'success',
};

const STATUS_LABELS: Record<ContactRequest['status'], string> = {
  new: 'Neu',
  read: 'Gelesen',
  replied: 'Beantwortet',
};

const AdminContacts: React.FC = () => {
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [selected, setSelected] = useState<ContactRequest | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactRequest | null>(null);

  const load = () => setContacts(getContacts().slice().reverse());

  useEffect(() => {
    load();
  }, []);

  const openDetail = (contact: ContactRequest) => {
    setSelected(contact);
    if (contact.status === 'new') {
      updateContactStatus(contact.id, 'read');
      load();
    }
  };

  const handleStatusChange = (id: string | number, status: ContactRequest['status']) => {
    updateContactStatus(id, status);
    load();
    if (selected && String(selected.id) === String(id)) {
      setSelected((prev) => prev ? { ...prev, status } : null);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteContact(deleteTarget.id);
    setDeleteTarget(null);
    if (selected && String(selected.id) === String(deleteTarget.id)) setSelected(null);
    load();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Kontaktanfragen
      </Typography>

      {contacts.length === 0 ? (
        <Alert severity="info">Noch keine Kontaktanfragen vorhanden.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ border: '1px solid #e8e8e8', boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Firma</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell sx={{ fontWeight: contact.status === 'new' ? 700 : 400 }}>
                    {contact.name}
                  </TableCell>
                  <TableCell>{contact.email}</TableCell>
                  <TableCell>{contact.company ?? '—'}</TableCell>
                  <TableCell>
                    {new Date(contact.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABELS[contact.status]}
                      color={STATUS_COLORS[contact.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(contact)} sx={{ mr: 0.5 }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: 'error.main' }}
                      onClick={() => setDeleteTarget(contact)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Anfrage von {selected.name}</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography><strong>E-Mail:</strong> {selected.email}</Typography>
                {selected.phone && <Typography><strong>Telefon:</strong> {selected.phone}</Typography>}
                {selected.company && <Typography><strong>Firma:</strong> {selected.company}</Typography>}
                <Typography><strong>Datum:</strong> {new Date(selected.created_at).toLocaleString('de-DE')}</Typography>
                <Box sx={{ bgcolor: '#f8f8f8', p: 2, borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.message}
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ mt: 1, minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selected.status}
                    label="Status"
                    onChange={(e) => handleStatusChange(selected.id, e.target.value as ContactRequest['status'])}
                  >
                    <MenuItem value="new">Neu</MenuItem>
                    <MenuItem value="read">Gelesen</MenuItem>
                    <MenuItem value="replied">Beantwortet</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSelected(null)}>Schließen</Button>
              <Button
                href={`mailto:${selected.email}`}
                variant="contained"
                onClick={() => handleStatusChange(selected.id, 'replied')}
              >
                Per E-Mail antworten
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Anfrage löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du die Anfrage von <strong>{deleteTarget?.name}</strong> wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Löschen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminContacts;
